import React, { useState, useEffect } from 'react';
import { X, Search, Mail, Send, Users, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import { Checkbox } from '../../ui/checkbox';
import { Label } from '../../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { supabase } from '../../../lib/supabaseClient';
import TemplateSelector from './TemplateSelector';
import { resolveMergeTags, resolveMergeTagsHtml } from '../../../utils/emailMergeTagResolver';
import DOMPurify from 'dompurify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const ManualCandidateSearch = ({ open, onOpenChange, rfq, gmailConnected }) => {
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [drafting, setDrafting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [individualTemplate, setIndividualTemplate] = useState(null);
  const [bulkTemplate, setBulkTemplate] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    clearance: '',
    state: '',
    availability: 'open'
  });

  useEffect(() => {
    if (open) {
      fetchCandidates();
    } else {
      // Reset on close
      setSelectedCandidates([]);
      setSearchTerm('');
      setSuccess(null);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    filterCandidates();
  }, [candidates, searchTerm, filters]);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('candidates')
        .select(`
          id,
          first_name,
          last_name,
          email,
          location_state,
          clearance_level,
          availability_status,
          candidate_skills(
            skill_id,
            skills_master(name)
          )
        `)
        .eq('status', 'Active')
        .not('email', 'is', null);

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      // Format candidate data
      const formattedCandidates = data.map(c => ({
        ...c,
        skills: c.candidate_skills?.map(cs => cs.skills_master?.name).filter(Boolean) || []
      }));

      setCandidates(formattedCandidates);
    } catch (err) {
      console.error('Error fetching candidates:', err);
      setError('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  };

  const filterCandidates = () => {
    let filtered = [...candidates];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        c.first_name?.toLowerCase().includes(term) ||
        c.last_name?.toLowerCase().includes(term) ||
        c.email?.toLowerCase().includes(term) ||
        c.skills?.some(s => s.toLowerCase().includes(term))
      );
    }

    // Clearance filter
    if (filters.clearance) {
      filtered = filtered.filter(c => c.clearance_level === filters.clearance);
    }

    // State filter
    if (filters.state) {
      filtered = filtered.filter(c => c.location_state === filters.state);
    }

    // Availability filter
    if (filters.availability) {
      filtered = filtered.filter(c => c.availability_status === filters.availability);
    }

    setFilteredCandidates(filtered);
  };

  const toggleCandidate = (candidateId) => {
    setSelectedCandidates(prev =>
      prev.includes(candidateId)
        ? prev.filter(id => id !== candidateId)
        : [...prev, candidateId]
    );
  };

  const selectAll = () => {
    setSelectedCandidates(filteredCandidates.map(c => c.id));
  };

  const deselectAll = () => {
    setSelectedCandidates([]);
  };

  const handleDraftEmails = async (mode) => {
    if (!gmailConnected) {
      setError('Gmail not connected. Please connect in Settings → Integrations');
      return;
    }

    if (selectedCandidates.length === 0) {
      setError('Please select at least one candidate');
      return;
    }

    const template = mode === 'individual' ? individualTemplate : bulkTemplate;
    if (!template) {
      setError(`Please select a template for ${mode} outreach`);
      return;
    }

    try {
      setDrafting(true);
      setError(null);
      setSuccess(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const selectedCandidateData = candidates.filter(c => selectedCandidates.includes(c.id));

      // Prepare RFQ data for templates
      const rfqData = {
        ref_code: rfq.ref_code,
        title: rfq.title,
        client_name: rfq.client?.name,
        locations: rfq.locations,
        clearance_required: rfq.clearance_required,
        work_arrangement: rfq.work_arrangement,
        estimated_start_date: rfq.estimated_start_date,
        initial_term_months: rfq.initial_term_months,
        description: rfq.description,
        client_close_datetime: rfq.client_close_datetime
      };

      if (mode === 'individual') {
        // Create individual drafts for each candidate
        let successCount = 0;

        for (const candidate of selectedCandidateData) {
          // Prepare template data with both candidate and RFQ info
          const templateData = {
            candidate: {
              first_name: candidate.first_name,
              last_name: candidate.last_name,
              email: candidate.email,
              location_state: candidate.location_state,
              clearance_level: candidate.clearance_level
            },
            rfq: rfqData,
            client: { name: rfq.client?.name },
            application_url: `${process.env.REACT_APP_SITE_URL || 'https://appsytec.com'}/apply/${rfq.id}`
          };

          // Resolve merge tags
          const emailSubject = resolveMergeTags(template.subject_template, templateData);
          const emailBodyHtml = resolveMergeTagsHtml(template.body_template, templateData);

          // Sanitize final HTML
          const sanitizedBody = DOMPurify.sanitize(emailBodyHtml);

          const response = await fetch(`${API_URL}/gmail/draft`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify({
              to: candidate.email,
              subject: emailSubject,
              body: sanitizedBody
            })
          });

          if (response.ok) {
            successCount++;
          }
        }

        setSuccess(`Successfully created ${successCount} draft${successCount !== 1 ? 's' : ''} in Gmail`);
      } else {
        // Create one email with BCC to all candidates
        const emails = selectedCandidateData.map(c => c.email).join(', ');

        // For bulk mode, we don't have a specific candidate, just RFQ info
        const templateData = {
          rfq: rfqData,
          client: { name: rfq.client?.name },
          application_url: `${process.env.REACT_APP_SITE_URL || 'https://appsytec.com'}/apply/${rfq.id}`
        };

        // Resolve merge tags
        const emailSubject = resolveMergeTags(template.subject_template, templateData);
        const emailBodyHtml = resolveMergeTagsHtml(template.body_template, templateData);

        // Sanitize final HTML
        const sanitizedBody = DOMPurify.sanitize(emailBodyHtml);

        // Get current user's email
        const { data: { user } } = await supabase.auth.getUser();

        const response = await fetch(`${API_URL}/gmail/draft`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            to: user.email, // Send to self
            bcc: emails, // BCC all candidates
            subject: emailSubject,
            body: sanitizedBody
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create draft');
        }

        setSuccess(`Successfully created draft in Gmail with ${selectedCandidates.length} candidate${selectedCandidates.length !== 1 ? 's' : ''} in BCC`);
      }

      // Auto-close after 2 seconds
      setTimeout(() => {
        onOpenChange(false);
      }, 2000);

    } catch (err) {
      console.error('Error drafting emails:', err);
      setError(err.message || 'Failed to create email drafts');
    } finally {
      setDrafting(false);
    }
  };

  if (!open) return null;

  const uniqueStates = [...new Set(candidates.map(c => c.location_state).filter(Boolean))].sort();
  const uniqueClearances = [...new Set(candidates.map(c => c.clearance_level).filter(Boolean))].sort();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Manual Candidate Search - {rfq.ref_code}</DialogTitle>
          <DialogDescription>
            Search and select candidates to draft outreach emails
          </DialogDescription>
        </DialogHeader>

        {/* Filters */}
        <div className="space-y-4 border-b pb-4">
          <div className="grid grid-cols-4 gap-3">
            <div>
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Name, email, skills..."
                  className="pl-9"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="clearance">Clearance</Label>
              <select
                id="clearance"
                value={filters.clearance}
                onChange={(e) => setFilters({ ...filters, clearance: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">All Clearances</option>
                {uniqueClearances.map(cl => (
                  <option key={cl} value={cl}>{cl}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="state">State</Label>
              <select
                id="state"
                value={filters.state}
                onChange={(e) => setFilters({ ...filters, state: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">All States</option>
                {uniqueStates.map(st => (
                  <option key={st} value={st}>{st}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="availability">Availability</Label>
              <select
                id="availability"
                value={filters.availability}
                onChange={(e) => setFilters({ ...filters, availability: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">All</option>
                <option value="open">Open</option>
                <option value="open_soon">Open Soon</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={selectAll}>
                Select All ({filteredCandidates.length})
              </Button>
              <Button variant="outline" size="sm" onClick={deselectAll}>
                Deselect All
              </Button>
              <span className="text-sm text-muted-foreground">
                {selectedCandidates.length} selected
              </span>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        {/* Candidates List */}
        <div className="flex-1 overflow-y-auto border rounded-md">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredCandidates.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No candidates found matching your criteria
            </div>
          ) : (
            <div className="divide-y">
              {filteredCandidates.map((candidate) => (
                <div
                  key={candidate.id}
                  className="p-4 hover:bg-muted/30 cursor-pointer flex items-start gap-3"
                  onClick={() => toggleCandidate(candidate.id)}
                >
                  <Checkbox
                    checked={selectedCandidates.includes(candidate.id)}
                    onCheckedChange={() => toggleCandidate(candidate.id)}
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium">
                        {candidate.first_name} {candidate.last_name}
                      </h4>
                      <div className="flex items-center gap-2">
                        {candidate.clearance_level && (
                          <Badge variant="outline" className="text-xs">
                            {candidate.clearance_level}
                          </Badge>
                        )}
                        {candidate.availability_status === 'open' && (
                          <Badge className="text-xs bg-green-100 text-green-700">
                            Available
                          </Badge>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{candidate.email}</p>
                    {candidate.location_state && (
                      <p className="text-sm text-muted-foreground mb-2">{candidate.location_state}</p>
                    )}
                    {candidate.skills.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {candidate.skills.slice(0, 5).map((skill, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {skill}
                          </Badge>
                        ))}
                        {candidate.skills.length > 5 && (
                          <Badge variant="secondary" className="text-xs">
                            +{candidate.skills.length - 5}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="space-y-4 pt-4 border-t">
          {/* Template Selectors */}
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="text-xs font-medium block mb-2 text-muted-foreground">
                Individual Outreach Template
              </label>
              <TemplateSelector
                templateType="candidate_individual_outreach"
                label="Select Template"
                onTemplateSelect={setIndividualTemplate}
                selectedTemplateId={individualTemplate?.id}
                variant="outline"
                size="sm"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs font-medium block mb-2 text-muted-foreground">
                Bulk Outreach Template
              </label>
              <TemplateSelector
                templateType="candidate_bulk_outreach"
                label="Select Template"
                onTemplateSelect={setBulkTemplate}
                selectedTemplateId={bulkTemplate?.id}
                variant="outline"
                size="sm"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {selectedCandidates.length} candidate{selectedCandidates.length !== 1 ? 's' : ''} selected
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={() => handleDraftEmails('individual')}
                disabled={drafting || selectedCandidates.length === 0 || !gmailConnected || !individualTemplate}
              >
                {drafting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Drafting...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Draft Individual Emails
                  </>
                )}
              </Button>
              <Button
                onClick={() => handleDraftEmails('bulk')}
                disabled={drafting || selectedCandidates.length === 0 || !gmailConnected || !bulkTemplate}
              >
                {drafting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Drafting...
                  </>
                ) : (
                  <>
                    <Users className="h-4 w-4 mr-2" />
                    Draft Bulk Email (BCC)
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ManualCandidateSearch;
